import { MAINTENANCE_CONSTANTS } from '@/constants/residents';

export type MaintenanceStep = 'issue' | 'details' | 'urgency' | 'access';

export type MaintenanceErrorField = 'subCategory' | 'severity' | 'title' | 'timeSlots';

export interface MaintenanceValidationInput {
  requestType: 'maintenance' | 'it';
  subCategoryOptions: string[];
  subCategory: string;
  severity: string;
  title: string;
  permissionChoice: string | null;
  timeSlots: string[];
}

export interface MaintenanceValidationResult {
  valid: boolean;
  errors: Partial<Record<MaintenanceErrorField, string>>;
  message?: string;
}

const MIN_SLOTS = MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS;

// Canonical order — drives both per-step checks and the first-error `message`.
const CHECKS: Array<{
  step: MaintenanceStep;
  field: MaintenanceErrorField;
  run: (i: MaintenanceValidationInput) => string | undefined;
}> = [
  {
    step: 'issue',
    field: 'subCategory',
    run: (i) =>
      i.requestType === 'maintenance' && i.subCategoryOptions.length > 0 && !i.subCategory
        ? 'Please select a subcategory.'
        : undefined,
  },
  {
    step: 'urgency',
    field: 'severity',
    run: (i) => (!i.severity ? 'Please select how this affects you.' : undefined),
  },
  {
    step: 'details',
    field: 'title',
    run: (i) => (!i.title.trim() ? 'Please enter a brief title.' : undefined),
  },
  {
    step: 'access',
    field: 'timeSlots',
    run: (i) =>
      i.permissionChoice === 'no' &&
      i.timeSlots.filter((s) => s.trim()).length < MIN_SLOTS
        ? `Please select at least ${MIN_SLOTS} time slots.`
        : undefined,
  },
];

export const validateMaintenanceForm = (
  input: MaintenanceValidationInput,
  step?: MaintenanceStep,
): MaintenanceValidationResult => {
  const errors: Partial<Record<MaintenanceErrorField, string>> = {};
  for (const check of CHECKS) {
    if (step && check.step !== step) continue;
    const msg = check.run(input);
    if (msg) errors[check.field] = msg;
  }
  const firstFailing = CHECKS.find((c) => errors[c.field]);
  return {
    valid: Object.keys(errors).length === 0,
    errors,
    message: firstFailing ? errors[firstFailing.field] : undefined,
  };
};
